import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { fetchTransactionLineItems } from '../CartPage.duck';
import { buildFetchLineItemsPayload, getNextAuthorId } from '../CartHelpers';

/**
 * Custom hook to manage the active cart's state and line items fetching.
 *
 * NOTE on keys: `items` is keyed by CART - a (store, date range) group key from
 * buildCartItemsByGroupKey - not by author, and every "authorId" below is really that
 * cart key (the names predate carts being split by date range; the hook only ever
 * uses the key as a map index, so the behaviour is unchanged). Line items in
 * CartPage.duck are stored under the same key.
 * 
 * Handles:
 * - Tracking which author cart is currently active (showing order summary)
 * - Only refetching line items when the active author's cart is updated
 * - Switching to next available author when active author's cart becomes empty
 * - Initializing with first author on mount
 * 
 * @param {Object} params
 * @param {Object} params.items - Cart items grouped by authorId
 * @param {Boolean} params.updateCartInProgress - Whether cart update is in progress
 * @param {Boolean} params.removeFromCartInProgress - Whether item removal is in progress
 */
export const useActiveAuthorCart = ({
    items,
    updateCartInProgress,
    removeFromCartInProgress,
    addToCartInProgress,
}) => {
    const dispatch = useDispatch();
    const [activeAuthorId, setActiveAuthorId] = useState(null);
    const [updatingAuthorId, setUpdatingAuthorIdState] = useState(null);

    // Use ref instead of state to avoid React batching race conditions
    // Ref updates are synchronous, ensuring the value is set before dispatch
    const updatingAuthorIdRef = useRef(null);

    const prevUpdateInProgress = useRef(updateCartInProgress);
    const prevRemoveInProgress = useRef(removeFromCartInProgress);
    const prevAddToCartInProgress = useRef(addToCartInProgress);
    const isInitialized = useRef(false);

    // Derived state - items for active author
    const activeItems = activeAuthorId ? items?.[activeAuthorId] || [] : [];

    // Fetch line items for an author
    const fetchLineItems = useCallback(
        (authorId, authorItems) => {
            if (!authorId || !authorItems?.length) return;

            const fetchLineItemsData = buildFetchLineItemsPayload(authorItems);
            // Skip dispatch if all items have deleted listings — nothing valid to price
            if (!fetchLineItemsData.length) return;
            dispatch(fetchTransactionLineItems(authorId, { fetchLineItemsData }));
        },
        [dispatch]
    );

    // Switch to a new active author
    const switchToAuthor = useCallback(
        (authorId, shouldFetch = true) => {
            setActiveAuthorId(authorId);
            if (shouldFetch && authorId && items?.[authorId]) {
                fetchLineItems(authorId, items[authorId]);
            }
        },
        [items, fetchLineItems]
    );

    // Handle cart updates completing
    useEffect(() => {
        const wasUpdating = prevUpdateInProgress.current;
        const isComplete = !updateCartInProgress;

        if (wasUpdating && isComplete) {
            const nextAuthorId = getNextAuthorId(items, activeAuthorId);
            const updatedKey = updatingAuthorIdRef.current;
            const activeStillExists = !!activeAuthorId && items?.[activeAuthorId]?.length > 0;

            if (!nextAuthorId) {
                // Cart is empty
                setActiveAuthorId(null);
            } else if (!activeStillExists && updatedKey && items?.[updatedKey]?.length > 0) {
                // The active cart's items were moved to new dates (inline date edit):
                // its old key is gone and the write was tagged with the key they now
                // live under - possibly a card that already existed, which they merged
                // into. Follow them there rather than falling back to "first cart".
                switchToAuthor(updatedKey);
            } else if (nextAuthorId !== activeAuthorId) {
                // Active author became empty, switch to next
                switchToAuthor(nextAuthorId);
            } else if (updatingAuthorIdRef.current === activeAuthorId) {
                // Update was for the active cart - refetch line items
                fetchLineItems(activeAuthorId, items[activeAuthorId]);
            } else if (updatingAuthorIdRef.current && items[updatingAuthorIdRef.current]) {
                // Update was for a non-active cart. Its card still shows a subtotal
                // priced from that cart's own line items, so re-price it too - used
                // to be skipped, which left the card showing a spinner and then the
                // OLD subtotal after a quantity change.
                fetchLineItems(updatingAuthorIdRef.current, items[updatingAuthorIdRef.current]);
            }

            // Clear the ref and state
            updatingAuthorIdRef.current = null;
            setUpdatingAuthorIdState(null);
        }

        prevUpdateInProgress.current = updateCartInProgress;
    }, [updateCartInProgress, activeAuthorId, items, switchToAuthor, fetchLineItems]);

    // Handle item removal completing
    useEffect(() => {
        const wasRemoving = prevRemoveInProgress.current;
        const isComplete = !removeFromCartInProgress;

        if (wasRemoving && isComplete && activeAuthorId) {
            const activeAuthorItems = items?.[activeAuthorId];
            const activeAuthorHasItems = activeAuthorItems?.length > 0;
            if (activeAuthorHasItems) {
                // Active author still has items - refetch line items
                fetchLineItems(activeAuthorId, activeAuthorItems);
            } else {
                // Active author's cart is now empty - switch to next available
                const nextAuthorId = getNextAuthorId(items, activeAuthorId);
                if (nextAuthorId) {
                    switchToAuthor(nextAuthorId);
                } else {
                    // No authors left - clear state
                    setActiveAuthorId(null);
                }
            }
        }

        prevRemoveInProgress.current = removeFromCartInProgress;
    }, [removeFromCartInProgress, activeAuthorId, items, switchToAuthor, fetchLineItems]);

    // Re-fetch line items when a new item is added to the cart so the breakdown
    // reflects the updated quantities immediately (bypasses the 60-second loadData cache).
    useEffect(() => {
        const wasAdding = prevAddToCartInProgress.current;
        const isComplete = !addToCartInProgress;

        if (wasAdding && isComplete && activeAuthorId) {
            const activeAuthorItems = items?.[activeAuthorId];
            if (activeAuthorItems?.length) {
                fetchLineItems(activeAuthorId, activeAuthorItems);
            }
        }

        prevAddToCartInProgress.current = addToCartInProgress;
    }, [addToCartInProgress, activeAuthorId, items, fetchLineItems]);

    // Initialize with first author on mount (items are pre-sorted by recency in duck)
    useEffect(() => {
        if (!isInitialized.current && Object.keys(items || {}).length > 0) {
            const firstAuthorId = Object.keys(items)[0];
            if (firstAuthorId) {
                switchToAuthor(firstAuthorId);
                isInitialized.current = true;
            }
        }
    }, [items, switchToAuthor]);

    // Setter function for the ref (synchronous, avoids React batching issues)
    const setUpdatingAuthorId = useCallback((authorId) => {
        updatingAuthorIdRef.current = authorId;
        setUpdatingAuthorIdState(authorId);
    }, []);

    return {
        activeAuthorId,
        activeItems,
        updatingAuthorId,
        setUpdatingAuthorId,
        switchToAuthor,
        fetchLineItems,
    };
};

